<template>
  <div class="me-roles">
    <div class="me-roles__header">
      <span class="me-roles__title">
        <v-icon size="16" class="me-roles__title-icon">mdi-account-key-outline</v-icon>
        {{ $t("settings.profile.roles") }}
      </span>
      <span class="me-roles__count">{{ roleKeys.length }}</span>
    </div>

    <div v-if="roleKeys.length" class="me-roles__chips">
      <span
        v-for="roleKey in roleKeys"
        :key="roleKey"
        class="me-role-chip"
        :class="`me-role-chip--${roleFamily(roleKey)}`"
      >
        <span class="me-role-chip__dot" />
        {{ roleKey }}
      </span>
    </div>
    <p v-else class="me-roles__empty">{{ $t("settings.profile.noRoles") }}</p>

    <!-- Vizibil doar pentru rolurile care chiar au acces la `/users/:id/roles`. -->
    <v-btn
      v-if="rolesPageLink"
      variant="outlined"
      block
      class="me-roles__cta"
      prepend-icon="mdi-shield-account-outline"
      :to="rolesPageLink"
    >
      {{ $t("settings.profile.viewRoles") }}
    </v-btn>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { canOpenUserRolesPage, roleFamily } from "@/components/userAccount/profileRoles.mjs";

export default defineComponent({
  name: "UserRolesCard",
  props: {
    roleKeys: {
      type: Array,
      default: () => [],
    },
    primaryRole: {
      type: String,
      default: "",
    },
    userId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    rolesPageLink() {
      if (!this.userId) return null;
      if (!canOpenUserRolesPage(this.roleKeys, this.primaryRole)) return null;
      return `/users/${this.userId}/roles`;
    },
  },
  methods: { roleFamily },
});
</script>

<style scoped>
.me-roles__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.me-roles__title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-text-strong);
}

.me-roles__title-icon {
  color: rgba(var(--v-theme-on-surface), 0.45);
}

.me-roles__count {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(var(--v-theme-on-surface), 0.5);
  font-variant-numeric: tabular-nums;
}

.me-roles__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.me-roles__empty {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.55;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.me-roles__cta {
  margin-top: 14px;
  border-radius: var(--app-radius-base) !important;
  text-transform: none;
  letter-spacing: 0.01em;
}

/* ── Role chip — tinted by module family ───────────────────── */
.me-role-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px 5px 9px;
  border-radius: var(--app-radius-pill);
  font-size: 0.74rem;
  font-weight: 600;
  font-family: var(--app-font-family-mono);
}

.me-role-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: currentColor;
  opacity: 0.85;
}

.me-role-chip--admin {
  background: rgba(var(--v-theme-primary), 0.1);
  border: 1px solid rgba(var(--v-theme-primary), 0.22);
  color: rgb(var(--v-theme-primary));
}

.me-role-chip--vacation {
  background: var(--app-info-tonal-bg);
  border: 1px solid var(--app-info-tonal-border);
  color: var(--app-info-tonal-text);
}

.me-role-chip--crm {
  background: var(--app-tertiary-soft-bg);
  border: 1px solid var(--app-tertiary-soft-border);
  color: var(--app-tertiary-soft-text);
}

.me-role-chip--core {
  background: var(--app-quaternary-soft-bg);
  border: 1px solid var(--app-quaternary-soft-border);
  color: var(--app-quaternary-soft-text);
}

.me-role-chip--ai {
  background: var(--app-secondary-soft-bg);
  border: 1px solid var(--app-secondary-soft-border);
  color: var(--app-secondary-soft-text);
}

.me-role-chip--neutral {
  background: var(--app-neutral-chip-tonal-bg);
  border: 1px solid var(--app-neutral-chip-tonal-border);
  color: var(--app-neutral-chip-tonal-text);
}
</style>
